import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIMask } from "react-imask";
import { useDispatch, useSelector } from "react-redux";
import { apiUrl, recaptchaKey } from "../../../data/constants";
import { isValidExpirationDate, formatPrice } from "../../../helpers/index";
import { trackFunnel } from "../../../helpers/analytics";

// import { postBooking } from "../../../redux/thunks/coursesThunk";
// import { Loader } from "../../../components/ui/Loader"

export const PaymentDetails = ({ prevHandler, nextHandler }) => {
  const [params] = useSearchParams();
  const parameters = ["domain", "utm_source", "utm_medium"];
  let utmParams = "";

  parameters.map((paramName) => {
    if (params.get(paramName)) {
      const paramValue = params.get(paramName);
      utmParams += "&" + paramName + "=" + paramValue;
    }
  });

  const maskOptions = {
    cardNumber: {
      mask: "0000 0000 0000 0000",
      lazy: false,
      placeholderChar: "-",
    },
    expirationDate: {
      mask: "00 / 00",
      lazy: false,
      placeholderChar: "-",
    },
    cardCvv: {
      mask: "000",
      lazy: false,
      placeholderChar: "-",
    },
  };

  const dispatch = useDispatch();
  const [loading, setLoading] = useState();
  const [isExpired, setIsExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const { internal_id } = useParams();
  const learners = useSelector((state) => state.courses.learners);
  const bookingType = useSelector((state) => state.courses.bookingType);

  const selectedCourse = useSelector((state) => state.courses.selectedCourse);

  const tags = useSelector((state) => state.courses.tags);
  const billingData = useSelector((state) => state.courses.billingData);
  const { access_token, instance_url } = useSelector((state) => state.courses);

  const expirationDateMask = useIMask(maskOptions.expirationDate);
  const cardNumberMask = useIMask(maskOptions.cardNumber);
  const cardCvvMask = useIMask(maskOptions.cardCvv);

  // We don't accept American Express (the Opayo account isn't Amex-enabled and
  // the scheme fee is too high). Detect it by IIN (Amex numbers start 34/37) so
  // both the card form and Google Pay give a clear message rather than an opaque
  // Opayo decline / Google wallet "not available for this merchant" error.
  const cardDigits = (cardNumberMask.unmaskedValue || "").replace(/\D/g, "");
  const isAmex = /^3[47]/.test(cardDigits);

  const inputFocus = (inputElem) => {
    setTimeout(() => {
      if (!inputElem?.value?.length || inputElem?.value?.includes("-")) {
        inputElem?.setSelectionRange(0, 0);
      }
      inputElem?.focus();
    }, 100);
  };

  useEffect(() => {
    setErrorMessage(null);
    setTimeout(() => {
      if (
        cardNumberMask.unmaskedValue.length &&
        !cardNumberMask.value.includes("-")
      ) {
        inputFocus(expirationDateMask.ref.current);
      }
    }, 50);
  }, [cardNumberMask.value]);

  useEffect(() => {
    setErrorMessage(null);
    setTimeout(() => {
      if (
        cardNumberMask.unmaskedValue.length &&
        !expirationDateMask.value.includes("-")
      ) {
        inputFocus(cardCvvMask.ref.current);
      }
    }, 50);
    setIsExpired(!isValidExpirationDate(expirationDateMask.unmaskedValue));
  }, [expirationDateMask.value]);
  useEffect(() => {
    setErrorMessage(null);
  }, [cardCvvMask.value]);
  // Funnel: any non-empty error message at the payment step = a payment failure.
  useEffect(() => {
    if (errorMessage) trackFunnel("payment_failed", { message: String(errorMessage).slice(0, 100) });
  }, [errorMessage]);
  useEffect(() => {
    setTimeout(() => {
      inputFocus(cardNumberMask.ref.current);
    }, 150);
    setTimeout(() => {
      inputFocus(cardNumberMask.ref.current);
    }, 200);
  }, []);

  // Shared payment plumbing (used by BOTH the card flow and the wallet flows):
  // the 3DS iframe dance, merchant-session-key fetch, and the submit call that
  // builds the exact payload the WP confirmation page + conversion tracking parse.
  const rp = apiUrl;

  function confirm3ds(params) {
      let url_params = "/" + btoa(JSON.stringify(params));
      return new Promise((res) => {
        let settled = false;
        let timer = null;
        function cleanup() {
          window.removeEventListener("message", messageHandler);
          if (timer) clearTimeout(timer);
          let el = document.querySelector(".iframe-3ds");
          if (el) el.remove();
          let c = document.querySelector(".iframe-3ds-cancel");
          if (c) c.remove();
        }
        function finish(value) {
          if (settled) return;
          settled = true;
          cleanup();
          res(value);
        }
        async function messageHandler(e) {
          // Only trust messages from our own origin (the API-rendered 3ds.html).
          if (e.origin && e.origin !== window.location.origin) return;
          if (e.data && e.data.iframe_3ds) {
            let payment_status = await fetch(
              `${rp}check-payment-status/${params.trans_id}`,
              { headers: { "Content-Type": "application/json" }, method: "GET" }
            )
              .then((x) => x.json())
              .catch(() => ({ success: false }));
            finish(payment_status);
          }
        }
        window.addEventListener("message", messageHandler);

        let iframe = document.createElement("iframe");
        iframe.classList.add("iframe-3ds");
        iframe.src = rp + `3ds` + url_params;
        document.body.append(iframe);

        // A dismissable Cancel so a stalled / abandoned bank challenge can never
        // strand the customer on an infinite spinner.
        let cancel = document.createElement("button");
        cancel.type = "button";
        cancel.className = "iframe-3ds-cancel";
        cancel.textContent = "Cancel";
        cancel.style.cssText =
          "position:fixed;top:14px;right:14px;z-index:2147483647;padding:8px 18px;border:0;border-radius:6px;background:#fff;color:#0d3b5b;box-shadow:0 2px 8px rgba(0,0,0,.25);cursor:pointer;font:600 14px sans-serif;";
        cancel.onclick = function () {
          finish({ success: false, cancelled: true });
        };
        document.body.append(cancel);

        // Safety timeout (5 min) — the bank challenge completes well within this.
        timer = setTimeout(function () {
          finish({ success: false, timeout: true });
        }, 5 * 60 * 1000);
      });
    }

    async function getSesKey() {
      return await fetch(`${rp}payments/makeMSK`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      }).then((x) => x.json());
    }

    async function submitPayment(ses_key, card_id, other_data = {}, hooks = {}) {
      const domainFromStorage = localStorage.getItem('domain');
      const users = learners.map((learner) => ({
        ...learner,
        ...billingData,
      }));

      let recaptchaToken = "";
      try {
        if (window.grecaptcha && window.grecaptcha.ready) {
          await new Promise((r) => window.grecaptcha.ready(r));
        }
        recaptchaToken = await window?.grecaptcha?.execute(recaptchaKey, {
          action: "payment",
        });
      } catch (e) {}
      if (!recaptchaToken) {
        // Retry once — a slow/late grecaptcha load shouldn't dead-end the customer.
        try {
          await new Promise((r) => setTimeout(r, 700));
          recaptchaToken = await window?.grecaptcha?.execute(recaptchaKey, {
            action: "payment",
          });
        } catch (e) {}
      }
      if (!recaptchaToken) {
        // The API (correctly) rejects empty tokens as bot traffic. Rather than
        // submit-and-fail with a dead-end "refresh the page", surface a retry here.
        trackFunnel("recaptcha_blocked");
        // Wallet flows: close the payment sheet with a failure verdict — otherwise
        // the Apple Pay sheet spins until Apple's ~30s timeout hides this message.
        try {
          if (hooks.onGatewayResult) hooks.onGatewayResult({ success: false });
        } catch (e) {}
        setLoading(false);
        setErrorMessage(
          "We couldn't verify your browser (this can happen on a slow connection or with a tracker blocker). Please wait a moment and tap PAY again."
        );
        return;
      }

      let data = {
        ses_key: ses_key,
        card_id: card_id,
        users: users,
        bookingType: bookingType,
        recaptchaToken: recaptchaToken,
        _other: {
          notificationURL: rp + "3ds",
          course_id: internal_id,
          course_price: selectedCourse.price_online,
          price: (
            selectedCourse.accept_deposit === 1
              ? +selectedCourse.price_deposite
              : +selectedCourse.price_online
          )
            ? selectedCourse.accept_deposit === 1
              ? +selectedCourse.price_deposite
              : +selectedCourse.price_online
            : 0, //TODO price
          course_title: selectedCourse.name,
          domain: domainFromStorage || "",
          ...tags,
        },
      };

      data = { ...data, ...other_data };

      let resp = await fetch(`${rp}payments/submit`, {
        headers: {
          Accept: "*/*",
          "content-type": "application/json",
        },
        body: JSON.stringify(data),
        method: "POST",
      }).then((r) => r.json());

      // Wallet flows: let the caller close the payment sheet with the gateway
      // verdict BEFORE any redirect / 3DS handling below.
      try {
        if (hooks.onGatewayResult) hooks.onGatewayResult(resp);
      } catch (e) {}

      // The confirmation ?hash is parsed by the WP booking-confirmation page and
      // conversion tracking. Wallet tokens are kilobytes and sensitive — exclude
      // them so the hash stays byte-compatible with the card flow's shape.
      const { wallet: _walletOmitted, ...hashData } = data;
      let hash = btoa(JSON.stringify(hashData)) + utmParams;
      if (resp.required_3ds) {
        let payment_status = await confirm3ds(resp.params_3ds);
        if (payment_status.success) {
          setLoading(false);
          trackFunnel("payment_success");
          window.location.replace(
            "https://www.skillstg.co.uk/booking-confirmation/?hash=" + hash
          );
        } else {
          setLoading(false);
          setErrorMessage(
            payment_status.timeout
              ? "Card authentication timed out. Your card was not charged — please tap PAY to try again."
              : payment_status.cancelled
              ? "Card authentication was cancelled. Your card was not charged — you can try again."
              : payment_status.message ||
                "Card authentication was not completed. Please try again or use a different card."
          );
        }
      } else {
        if (resp.success) {
          cardNumberMask.setUnmaskedValue("");
          expirationDateMask.setUnmaskedValue("");
          cardCvvMask.setUnmaskedValue("");
          setLoading(false);

          trackFunnel("payment_success");
          window.location.replace(
            "https://www.skillstg.co.uk/booking-confirmation/?hash=" + hash
          );
        } else {
          setLoading(false);
          // Keep the entered card so the customer can correct/retry, and show the
          // gateway's real reason instead of a bare "Payment failed".
          setErrorMessage(
            resp.message ||
              "Your payment was declined. Please check your card details or try a different card."
          );
        }
      }
    }

  const submitPay = async (e) => {
    e.preventDefault();

    if (loading) {
      return false;
    }

    if (isAmex) {
      setErrorMessage(
        "Sorry, we don't accept American Express. Please pay with a Visa or Mastercard."
      );
      return false;
    }

    setErrorMessage(null);
    setLoading(true);
    trackFunnel("payment_attempt");

    let { ses_key } = await getSesKey();

    let result = await new Promise(function (res, rej) {
      window
        .sagepayOwnForm({ merchantSessionKey: ses_key, environment: (window.SAGEPAY_ENV === 'live') ? 'live' : 'test' })
        .tokeniseCardDetails({
          cardDetails: {
            cardholderName:
              learners[0]?.firstName + " " + learners[0]?.lastName,
            cardNumber: cardNumberMask.unmaskedValue.split(" ").join(""),
            expiryDate: expirationDateMask.unmaskedValue.split(" / ").join(""),
            securityCode: cardCvvMask.unmaskedValue,
          },
          onTokenised: function (result) {
            res(result);
          },
        });
    });

    if (!result.success) {
      setLoading(false);
      setErrorMessage("ERROR: " + result.errors[0].message);
      return false;
    }
    let other_data = {
      wwidth: window.innerWidth,
      wheight: window.innerHeight,
      tz: new Date().getTimezoneOffset(),
    };

    await submitPayment(ses_key, result.cardIdentifier, other_data);

    setLoading(false);
  };

  // ---------------- Digital wallets (Apple Pay / Google Pay) ----------------
  // LIVE for all customers: Apple Pay (Safari) since 2026-07-04, Google Pay
  // (Chrome/Android) since 2026-07-09. Each wallet replaces card ENTRY only — the
  // booking contact and joining-instruction emails ALWAYS come from the form's
  // billing details, so no email/contact fields are requested from either wallet
  // sheet. Whether a button shows is decided by the platform itself
  // (ApplePaySession capability / Google isReadyToPay), never a flag.

  // Must stay in lock-step with the SERVER's charge rule (inc/sagepay.js
  // submitPayment): the API drops blank learner rows (same firstName/lastName/
  // emailAddress test) BEFORE counting seats, so the wallet sheet total must
  // count only non-blank rows or the biometrically-authorized amount could
  // differ from the captured amount.
  const walletSeats = () => {
    const filled = learners.filter(
      (u) =>
        u &&
        (String(u.firstName || "").trim() ||
          String(u.lastName || "").trim() ||
          String(u.emailAddress || "").trim())
    );
    return filled.length > 1 ? filled.length - 1 : 1;
  };
  const walletAmount = () =>
    (selectedCourse?.accept_deposit === 1
      ? +(selectedCourse?.price_deposite || 0)
      : +(selectedCourse?.price_online || 0)) * walletSeats();

  const walletOtherData = (wallet) => ({
    wwidth: window.innerWidth,
    wheight: window.innerHeight,
    tz: new Date().getTimezoneOffset(),
    wallet: wallet,
  });

  // Apple Pay is LIVE for all customers (validated end-to-end 2026-07-04:
  // 12p capture F2A07A8A…, SF booking ok, form-email rule verified).
  // Google Pay stays behind the dark-launch flag until the Google Pay & Wallet
  // Console registration is approved (TEST tokens decline on the live account).
  const applePayAvailable =
    typeof window !== "undefined" &&
    !!window.ApplePaySession &&
    window.ApplePaySession.canMakePayments();

  const onApplePay = () => {
    if (loading) return;
    setErrorMessage(null);
    trackFunnel("payment_attempt", { method: "applePay" });

    // ApplePaySession MUST be constructed synchronously inside the tap handler
    // (any await first throws InvalidAccessError). Slow work happens in callbacks.
    const request = {
      countryCode: "GB",
      currencyCode: "GBP",
      supportedNetworks: ["visa", "masterCard", "amex", "maestro"],
      merchantCapabilities: ["supports3DS"], // never supportsEMV (Opayo declines 6116)
      total: {
        label: "Skills Training Group",
        amount: walletAmount().toFixed(2),
      },
      // Deliberately NO requiredBillingContactFields / requiredShippingContactFields.
    };
    let session;
    try {
      session = new window.ApplePaySession(6, request);
    } catch (e) {
      setErrorMessage("Apple Pay could not start — please pay by card below.");
      return;
    }
    let sessionRef = null;

    session.onvalidatemerchant = async () => {
      try {
        const r = await fetch(`${rp}payments/applepay/session`, {
          headers: { "Content-Type": "application/json" },
          method: "POST",
          body: JSON.stringify({}),
        }).then((x) => x.json());
        if (!r || !r.success || !r.merchantSession) {
          throw new Error((r && r.message) || "merchant validation failed");
        }
        sessionRef = r.sessionRef;
        session.completeMerchantValidation(r.merchantSession);
      } catch (e) {
        try { session.abort(); } catch (_) {}
        setLoading(false);
        setErrorMessage("Apple Pay is temporarily unavailable — please pay by card below.");
      }
    };

    session.onpaymentauthorized = async (event) => {
      try {
        setLoading(true);
        const { ses_key } = await getSesKey();
        await submitPayment(
          ses_key,
          null,
          walletOtherData({
            type: "applePay",
            token: event.payment.token,
            sessionRef: sessionRef,
          }),
          {
            onGatewayResult: (resp) => {
              try {
                session.completePayment(
                  resp && (resp.success || resp.required_3ds)
                    ? window.ApplePaySession.STATUS_SUCCESS
                    : window.ApplePaySession.STATUS_FAILURE
                );
              } catch (e) {}
            },
          }
        );
      } catch (e) {
        try { session.completePayment(window.ApplePaySession.STATUS_FAILURE); } catch (_) {}
        setErrorMessage("Apple Pay could not complete — please pay by card below.");
      } finally {
        setLoading(false);
      }
    };

    session.oncancel = () => {
      setLoading(false);
    };

    session.begin();
  };

  // Google Pay is LIVE: the Google Pay & Wallet Console registration for
  // booking.skillstg.co.uk was approved 2026-07-09, so PRODUCTION tokens are
  // decryptable by the live Opayo account. merchantInfo.merchantId (below) is the
  // required Google console merchant id (BCR2DN…); the gatewayMerchantId in the
  // tokenizationSpecification is the separate Opayo gateway id and stays as-is.
  const GPAY_ENVIRONMENT = "PRODUCTION";
  const [gpayReady, setGpayReady] = useState(false);
  const gpayContainerRef = React.useRef(null);
  const gpayClientRef = React.useRef(null);
  const gpayHandlerRef = React.useRef(() => {});

  const onGooglePay = async () => {
    if (loading) return;
    setErrorMessage(null);
    trackFunnel("payment_attempt", { method: "googlePay" });
    try {
      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: "CARD",
            parameters: {
              allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
              allowedCardNetworks: ["VISA", "MASTERCARD"],
            },
            tokenizationSpecification: {
              type: "PAYMENT_GATEWAY",
              parameters: {
                gateway: "opayoelavon",
                gatewayMerchantId: "a4684e96-dadf-4175-af0a-12f48784179d",
              },
            },
          },
        ],
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPrice: walletAmount().toFixed(2),
          currencyCode: "GBP",
          countryCode: "GB",
        },
        merchantInfo: {
          merchantId: "BCR2DN5TZ7CKJKDZ",
          merchantName: "Skills Training Group",
        },
        // emailRequired deliberately NOT set — emails come from the form only.
      };
      const paymentData = await gpayClientRef.current.loadPaymentData(paymentDataRequest);
      setLoading(true);
      const { ses_key } = await getSesKey();
      await submitPayment(
        ses_key,
        null,
        walletOtherData({
          type: "googlePay",
          token: paymentData.paymentMethodData.tokenizationData.token,
        })
      );
      setLoading(false);
    } catch (e) {
      setLoading(false);
      if (e && e.statusCode === "CANCELED") {
        // Both a genuine user cancel AND Chrome environment-refusals ("Cannot show
        // PaymentRequest UI in a preview page or a background tab") surface as
        // CANCELED. Stay silent to the user (it's the expected cancel UX), but log
        // it so a button that looks dead is diagnosable in the field.
        console.warn(
          "[GooglePay] loadPaymentData CANCELED:",
          (e && e.statusMessage) || ""
        );
      } else {
        setErrorMessage("Google Pay could not complete — please pay by card below.");
      }
    }
  };

  useEffect(() => {
    gpayHandlerRef.current = onGooglePay;
  });

  useEffect(() => {
    // Only wire up Google Pay when online payment is actually enabled (this
    // mirrors PAYMENTS_ENABLED, recomputed here because that const is declared
    // further down): keeps pay.js off — and a live isReadyToPay call out of — the
    // "book by phone" fallback screen shown while SAGEPAY_ENV isn't "live".
    const paymentsOn =
      (typeof window !== "undefined" && window.SAGEPAY_ENV === "live") ||
      new URLSearchParams(window.location.search).get("paytest") === "stg-2026";
    if (!paymentsOn) return;
    let cancelled = false;
    const setup = () => {
      if (cancelled || !window.google?.payments?.api) return;
      try {
        const client = new window.google.payments.api.PaymentsClient({
          environment: GPAY_ENVIRONMENT,
        });
        gpayClientRef.current = client;
        client
          .isReadyToPay({
            apiVersion: 2,
            apiVersionMinor: 0,
            allowedPaymentMethods: [
              {
                type: "CARD",
                parameters: {
                  allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                  allowedCardNetworks: ["VISA", "MASTERCARD"],
                },
              },
            ],
          })
          .then((r) => {
            if (cancelled || !r.result || !gpayContainerRef.current) return;
            const btn = client.createButton({
              buttonColor: "black",
              buttonType: "pay",
              buttonSizeMode: "fill",
              onClick: () => gpayHandlerRef.current(),
            });
            gpayContainerRef.current.innerHTML = "";
            gpayContainerRef.current.appendChild(btn);
            setGpayReady(true);
          })
          .catch(() => {});
      } catch (e) {}
    };
    let s = null;
    if (window.google?.payments?.api) {
      setup();
    } else {
      // Reuse a pending script tag from a prior mount (addEventListener, never
      // s.onload=, so we can't clobber another mount's handler) — avoids
      // double-injection when the step unmounts before pay.js finishes loading.
      s = document.querySelector('script[src="https://pay.google.com/gp/p/js/pay.js"]');
      if (!s) {
        s = document.createElement("script");
        s.src = "https://pay.google.com/gp/p/js/pay.js";
        s.async = true;
        document.head.appendChild(s);
      }
      s.addEventListener("load", setup);
    }
    return () => {
      cancelled = true;
      if (s) s.removeEventListener("load", setup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payments are enabled only when SAGEPAY_ENV is "live" (set via nginx sub_filter at go-live).
  // Until then customers are routed to phone; append ?paytest=stg-2026 to exercise the
  // sandbox payment flow on the live domain without exposing it to real customers.
  const PAYMENTS_ENABLED =
    (typeof window !== "undefined" && window.SAGEPAY_ENV === "live") ||
    new URLSearchParams(window.location.search).get("paytest") === "stg-2026";

  if (!PAYMENTS_ENABLED) {
    return (
      <div>
        <h3 className="mb-30">Complete your booking by phone</h3>
        <div className="mb-30">
          <p>
            Online card payment is temporarily unavailable while we complete a short
            upgrade. To secure your place
            {selectedCourse?.name ? ` on ${selectedCourse.name}` : ""}, please call our
            bookings team on{" "}
            <a href="tel:08081642780">
              <strong>0808 164 2780</strong>
            </a>{" "}
            and quote your chosen course and date. Apologies for the inconvenience.
          </p>
        </div>
        <div className="page-action d-flex align-items-center justify-content-between">
          <button
            onClick={prevHandler}
            className="btn btn--outline-secondary btn--md radius-5 ttu min-w-120"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <h3 className="mb-30">Payment Details</h3>
      {/* The gpay container must always be in the DOM so Google's async
          isReadyToPay() can attach the button for a Chrome/Android visitor who
          has no Apple Pay. Spacing/divider appear only once a wallet is present. */}
      <div className={applePayAvailable || gpayReady ? "mb-30" : ""}>
        <style>{`.apple-pay-button{-webkit-appearance:-apple-pay-button;-apple-pay-button-type:buy;-apple-pay-button-style:black;width:100%;height:46px;border-radius:5px;border:none;cursor:pointer;display:block;}`}</style>
        {applePayAvailable && (
          <button
            type="button"
            className="apple-pay-button"
            aria-label="Pay with Apple Pay"
            onClick={onApplePay}
            disabled={loading}
            style={{ marginBottom: "10px" }}
          />
        )}
        <div ref={gpayContainerRef} style={{ width: "100%" }} />
        {(applePayAvailable || gpayReady) && (
          <div
            style={{
              textAlign: "center",
              margin: "14px 0 0",
              color: "#5a6b78",
              fontSize: "14px",
            }}
          >
            or pay by card below
          </div>
        )}
      </div>
      <div className="row gutters-16 mb-30">
        <div className="col-12">
          <div className="form-group">
            <label className="form-group-title">
              Card Number<span className="color-danger ms-1">*</span>
            </label>
            <input
              ref={cardNumberMask.ref}
              className={`input ${isAmex ? "error" : ""}`}
              type="text"
              autoComplete="cc-number"
              inputMode="numeric"
              autoFocus
            />
            {isAmex ? (
              <span
                className="color-danger"
                style={{ display: "block", marginTop: "6px", fontSize: "13px" }}
              >
                Sorry, we don't accept American Express — please use a Visa or Mastercard.
              </span>
            ) : (
              <span
                style={{
                  display: "block",
                  marginTop: "6px",
                  fontSize: "13px",
                  color: "#80858a",
                }}
              >
                We accept Visa and Mastercard.
              </span>
            )}
          </div>
        </div>
        <div className="col-lg-7">
          <div className="row gutters-10">
            <div className="col-md-7">
              <div className="form-group">
                <label className="form-group-title">
                  Expiration Date<span className="color-danger ms-1">*</span>
                </label>
                <input
                  ref={expirationDateMask.ref}
                  className={`input ${isExpired ? "error" : ""}`}
                  type="text"
                  autoComplete="cc-exp"
                  inputMode="numeric"
                />
                {isExpired && (
                  <span className="color-danger">Card expired</span>
                )}
              </div>
            </div>
            <div className="col-md-5">
              <div className="form-group">
                <label className="form-group-title">
                  CVC<span className="color-danger ms-1">*</span>
                </label>
                <input ref={cardCvvMask.ref} className="input" type="text" autoComplete="cc-csc" inputMode="numeric" />
              </div>
            </div>
          </div>
          {errorMessage ? (
            <div className="mb-30 mt-10">
              <p style={{ color: "red" }}>{errorMessage}</p>
            </div>
          ) : null}
        </div>
      </div>
      <div className="page-action d-flex align-items-center justify-content-between">
        <button
          type="button"
          onClick={prevHandler}
          className="btn btn--outline-secondary btn--md radius-5 ttu min-w-120"
        >
          Back
        </button>
        <button
          className={`btn btn--success btn--md radius-5 ttu min-w-240 ${
            loading || isExpired || isAmex ? "disabled" : ""
          }`}
          style={{ minHeight: "46px", opacity: isExpired || isAmex ? "0.7" : "1" }}
          type="button"
          onClick={submitPay}
        >
          {loading ? (
            "Loading..."
          ) : (
            <>
              PAY £
              {formatPrice(
                (selectedCourse?.accept_deposit === 1
                  ? +(selectedCourse?.price_deposite || 0).toFixed(2)
                  : +(selectedCourse?.price_online || 0).toFixed(2)) *
                  (learners.length > 1 ? learners.length - 1 : learners.length)
              )}
            </>
          )}
        </button>
      </div>
    </form>
  );
};
